import { setProperties } from '@ember/object';
import { sort } from '@ember/object/computed';
import { belongsTo, hasMany, attr } from '@ember-data/model';
import { isBlank } from 'sh-www/utils/misc';
import { COMPANY } from './company';
import ShModel from './sh-model';

const APPROVAL = {
  UN_APPROVED: -1,
  UNDEFINED: 0,
  APPROVED: 1,
};

const SELLER_SIGNUP_STEPS = {
  EMAIL_PASSWORD: 'email_password',
  VALIDATE_EMAIL: 'validate_email',
  BASIC_PROFILE: 'basic_profile',
  EXTENDED_PROFILE: 'extended_profile',
  WORK_SAMPLES: 'work_samples',
  DONE: 'done',
};

const TYPES = {
  GUEST: 0,
  SELLER: 1,
  BUYER: 2,
  ADMIN: 3,
};

export const USER = {
  APPROVAL,
  TYPES,
  SELLER_SIGNUP_STEPS,
};

const BLOCK_USER_EVENTS_SORTING = ['createdAt:desc'];

class User extends ShModel {
  //Attributes
  @attr('string') firstName;
  @attr('string') middleName;
  @attr('string') lastName;
  @attr('string') formalName;
  @attr('string') slug;
  @attr('string') publicSlug;
  @attr('string') email;
  @attr('string') description;
  @attr('boolean') funnel;
  @attr('string') phone;
  @attr() password;
  @attr('number') rating;
  @attr('number') mrating;
  @attr('number') reviewCount;
  @attr('boolean') select;
  @attr('number') type;
  @attr('string') locationId;
  @attr('number') completedProjectCount;
  @attr('number') homeDistance;
  @attr('number') currentDistance;

  get heartbeatId() {
    return this.id;
  }

  @attr('string') signupFunnel;
  @attr('string') signupStep;
  @attr('boolean') hasPassword;
  @attr('boolean') hasCredentials;
  @attr('date') deletionRequestedAt;
  @attr('date') createdAt;
  @attr('date') updatedAt;

  get isCompanyAdmin() {
    return Boolean(this.companyMembership?.admin);
  }

  get isCompanyEditor() {
    return Boolean(this.companyMembership?.editor);
  }

  @attr('string') hyperwalletStatus;
  @attr('string') taxFormBaseStatus;
  @attr('object-to-array') taxFormSubStatuses;

  //Flag Attributes
  @attr('number') approval;
  @attr('boolean') buyerIsInvited;
  @attr('boolean') canViewReports;
  @attr('boolean') hasAssignments;
  @attr('boolean') inCurrentUsersNetwork;
  @attr('boolean') inNetwork;
  @attr('boolean') isAdmin;
  @attr('boolean') isVetted;
  @attr('boolean') isWhitelisted;

  get approved() {
    return this.approval === USER.APPROVAL.APPROVED;
  }

  get isSeller() {
    return this.type === 1;
  }

  get isBuyer() {
    return this.type === 2;
  }

  @attr('boolean') isPublic;
  @attr('string') blockedNote;
  @attr('boolean') superAdmin;
  @attr('boolean') verified;

  get actingAsBuyer() {
    return this.type === 2;
  }

  get actingAsSeller() {
    return this.type === 1;
  }

  get actingAsAdmin() {
    return this.type === 3;
  }

  //Perspective Properties
  @attr('boolean') isBlocked; // true when the perspective (current user) is blocked by the target company
  @attr('boolean') isBlockedBy; // will be true when the perspective (current user) has been blocked by the target user.

  //Relationships
  @hasMany('action', { async: false, inverse: 'actionable' }) actions;
  @hasMany('application', { async: false }) applications;
  @belongsTo('image', { async: false }) banner;
  @belongsTo('company', { async: false, inverse: 'approvedEmployees' }) company;
  @belongsTo('companyMembership', { async: false }) companyMembership;
  @hasMany('companyTeam', { async: false }) companyTeams;
  @hasMany('classification', { async: false }) experiences;
  @belongsTo('projectdigest', { async: false }) firstProject;
  @belongsTo('image', { async: false }) image;
  @hasMany('invite', { async: false }) invites;
  @belongsTo('last-location', { async: false }) lastLocation;
  @belongsTo('location', { async: false }) location;
  @hasMany('payoutAccount', { async: false }) payoutAccounts;
  @belongsTo('payoutAccount', { async: false }) preferredPayoutAccount;
  @belongsTo('profile', { async: false }) profile;
  @belongsTo('sellerApplicationStat', { async: false }) sellerApplicationStats;
  @belongsTo('sellerPostingStat', { async: false }) sellerPostingStats;
  @belongsTo('sellerProjectStat', { async: false }) sellerProjectStats;
  @belongsTo('sellerLatestReportStat', { async: false }) sellerLatestReportStats;
  @belongsTo('social-profile', { async: false }) socialProfile;
  @hasMany('shPayment', { async: false, inverse: 'user' }) shPayments;
  @hasMany('userExperiment', { async: false }) userExperiments;
  @belongsTo('userStatistic', { async: false }) userStatistics;

  get topFiveProjects() {
    return this.projectdigests.slice(0, 5);
  }

  @hasMany('activityFeedItem', { async: false }) activityFeedItems;

  /**
   * conditional - load with company-commands
   */
  @attr() applicationMeta;
  @hasMany('blockUserEvent', { async: false, inverse: 'user', }) blockUserEvents;
  @hasMany('shGroup', { async: false, inverse: 'owner' }) groupsOwned;
  @hasMany('shGroupMember', { async: false }) groupMemberships;
  @belongsTo('feeStructure', { async: false }) hiringFeeStructure;
  @hasMany('preAssignment', { async: false }) preAssignments;
  @hasMany('projectdigest', { async: false }) projectdigests;
  @hasMany('userDevice', { async: false }) devices;
  @hasMany('brand', { async: false, inverse: null }) workedWithBrands;

  /**
   * conditional - load with note-commands
   */
  @hasMany('note', { async: false, inverse: 'createdByUser' }) notesCreated;
  /**
   * conditional - load with note-commands
   */
  @hasMany('note', { async: false, inverse: 'createdAboutUser' }) notesAbout;
  @hasMany('notificationPreference', { async: false, inverse: 'user' }) emailNotificationPreferences;
  @hasMany('shGroup', { async: false, inverse: null }) companyGroupsOwned;

  // if a company doesn't have this feature we will always show them the link and do some upsell stuff
  get canViewReportsData() {
    if (this.isSeller) {
      return this.canViewReports;
    }

    return this.canViewReports && this.company.canUseReports;
  }

  /**
   * @type {Boolean}
   */
  get isDiyUser() {
    return (this.company.tier === 1) && this.isBuyer;
  }

  /**
   * @type {Boolean} Whether the user can see the upgrade menu item.
   */
  get canSeeUpgradeMenuItem() {
    return this.isBuyer && this.company?.tier === COMPANY.TIERS.FREE;
  }

  //Credits and Projects are 2 different ways of accessing the similar data.
  //A Credit connects projects<->users
  @hasMany('credit') credits;
  @hasMany('user', { inverse: null }) inviters;
  @hasMany('project') projects;
  @hasMany('service') services;
  @hasMany('serviceUserScore') serviceUserScores;
  @hasMany('serviceCompanyUserScore') serviceCompanyUserScores;
  @hasMany('tag') tags;
  @hasMany('companySellerConnection', { async: false }) companySellerConnections;

  @attr('boolean') isVettable;
  @attr('boolean') isAwaitingVettable;

  get servicesProvided() {
    const providedServiceIds = this.serviceCompanyUserScores.map(({ serviceId }) => serviceId);
    return this.services.reduce((acc, { name, id }) => {
      acc.push({ id, name, provided: providedServiceIds.includes(+id) });
      return acc;
    }, []).sort((a, b) => b.provided - a.provided);
  }

  get blockUserEventsSorting() {
    return BLOCK_USER_EVENTS_SORTING;
  }

  /**
   * NOTE - conditional - must have blockUserEvents loaded
   * @type {Ember.MutableArray<BlockUserEvent>}
   */
  @sort('blockUserEvents', 'blockUserEventsSorting')
  blockUserEventsSorted;

  get canBuy() {
    return this.type === 2;
  }

  /**
   * @type {Boolean}    True if the user CAN send direct messages.
   */
  canUserMessage() {
    if (this.type === USER.TYPES.SELLER) {
      return true;
    }

    return this.company.canDirectMessage;
  }

  get fullName() {
    const firstName = this.firstName || '';
    const lastName = this.lastName || '';

    if (isBlank(firstName) && isBlank(lastName)) {
      return '';
    }

    return `${firstName} ${lastName}`;
  }

  set fullName(value) {
    const fullName = value.split(' ');
    const firstName = fullName.shift();
    const lastName = fullName.join(' ');
    setProperties(this, { firstName, lastName });
  }

  /**
   * Get an identifier when dealing with users who may not have a name
   * ex. currently invited, signup in progress, etc.
   */
  get fullNameOrEmail() {
    return this.fullName.length > 0 ? this.fullName : this.email;
  }

  /**
   * Get a full name with a single character last name.
   *
   * @type {String}
   */
  get fullNameSingleLastDigit() {
    if (!this.lastName) {
      return this.firstName;
    }

    return `${this.firstName} ${this.lastName[0]}.`;
  }

  /**
   * NOTE - conditional - must have invites loaded
   * @type {Ember.MutableArray<Invite>}
   */
  get pendingInvites() {
    return this.invites.filter((invite) => !invite.isAccepted);
  }

  get name() {
    return this.fullName;
  }

  set name(value) {
    const fullName = value.split(' ');
    const firstName = fullName.shift();
    const lastName = fullName.join(' ');
    setProperties(this, { firstName, lastName });
  }

  /**
   * Relative path to the user's profile
   * @type {String}
   */
  get path() {
    return this.slug ? `/${this.slug}` : `/${this.id}`;
  }

  /**
   * Does the user still need to perform some action for tax purposes
   */
  get taxInformationNeedsAction() {
    if (!this.isSeller) {
      return false;
    }

    switch (this.taxFormBaseStatus) {
      case 'SUBMITTED':
        return false;
      case 'NOT_SUBMITTED':
      default:
        return true;
      case 'PENDING_REVIEW':
        return false;
    }
  }

  /**
   * Is tax information submitted and fully approved
   */
  get taxInformationComplete() {
    if (!this.isSeller) {
      return true;
    }

    if (this.taxFormBaseStatus !== 'SUBMITTED') {
      return false;
    }

    if (this.taxFormSubStatuses.length !== 1) {
      return false;
    }

    return !!this.taxFormSubStatuses.find(subStatus => subStatus === 'TIN_VALIDATED');
  }

  get underReview() {
    if (!this.isSeller) return false;
    if (this.isWhitelisted) return false;
    if (!this.isVettable) return false;
    if (!this.isVetted) return true;
    return false;
  }

  /**
   *
   */
  get whitelistedAndUnvetted() {
    return this.isWhitelisted && !this.isVetted;
  }
}

export default User;
